// src/components/PostPerformanceChart.tsx

import {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  LabelList,
} from "recharts";

import {
  TimeRange,
  filterByTimeRange,
} from "./timeRange";

interface PostRow {
  account: string;
  postUrl: string | null;
  title: string | null;
  date: string;
  impressions: number;
  clicks: number;
  ctr: number;
  likes: number;
  comments: number;
  shares: number;
  engagementRate: number;
}

interface Metric {
  key: keyof Omit<
    PostRow,
    | "account"
    | "postUrl"
    | "title"
    | "date"
  >;
  label: string;
  isPercentValue?: boolean;
}

const METRICS: Metric[] = [
  {
    key: "impressions",
    label: "Impressions",
  },
  {
    key: "clicks",
    label: "Clicks",
  },
  {
    key: "ctr",
    label: "CTR",
    isPercentValue: true,
  },
  {
    key: "likes",
    label: "Likes",
  },
  {
    key: "comments",
    label: "Comments",
  },
  {
    key: "shares",
    label: "Shares",
  },
  {
    key: "engagementRate",
    label: "Engagement Rate",
    isPercentValue: true,
  },
];

interface PostPerformanceChartProps {
  refreshKey: number;
  accountOptions: string[];
  timeRange: TimeRange;
}

export default function PostPerformanceChart({
  refreshKey,
  accountOptions,
  timeRange,
}: PostPerformanceChartProps) {
  const [
    selectedAccount,
    setSelectedAccount,
  ] = useState<string>(
    accountOptions[0] ?? ""
  );

  const [
    rawPosts,
    setRawPosts,
  ] = useState<PostRow[]>([]);

  const [
    isLoading,
    setIsLoading,
  ] = useState(true);

  const [
    error,
    setError,
  ] = useState<string | null>(
    null
  );

  const [
    postIndex,
    setPostIndex,
  ] = useState(0);

  useEffect(() => {
    if (!selectedAccount) {
      return;
    }

    setIsLoading(true);
    setError(null);

    fetch(
      `/api/posts?account=${encodeURIComponent(
        selectedAccount
      )}`
    )
      .then((res) => {
        if (!res.ok) {
          throw new Error(
            `Fehler beim Laden (Status ${res.status})`
          );
        }

        return res.json();
      })
      .then((rows: PostRow[]) => {
        setRawPosts(rows);
        setPostIndex(0);
      })
      .catch((err) => {
        setError(err.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    selectedAccount,
    refreshKey,
  ]);

  const filteredPosts = useMemo(
    () =>
      filterByTimeRange(
        rawPosts,
        timeRange
      ),
    [
      rawPosts,
      timeRange,
    ]
  );

  // Neueste Posts zuerst
  const sortedPosts = useMemo(
    () =>
      [...filteredPosts].sort(
        (a, b) =>
          b.date.localeCompare(
            a.date
          )
      ),
    [filteredPosts]
  );

  const currentPost =
    sortedPosts[
      Math.min(
        postIndex,
        sortedPosts.length - 1
      )
    ];

  /*
   * Ermittelt für jede Kennzahl den höchsten Wert
   * innerhalb der aktuell gefilterten Posts.
   *
   * Beispiel:
   * höchste Likes = 100
   * aktueller Post = 80 Likes
   * -> Balkenbreite = 80 %
   *
   * Die 80 % werden nur intern für die Darstellung
   * verwendet. Angezeigt wird weiterhin "80".
   */
  const metricMaximums = useMemo(() => {
    const result: Record<
      string,
      number
    > = {};

    for (const metric of METRICS) {
      let maxValue = 0;

      for (const post of filteredPosts) {
        const value =
          post[metric.key] ?? 0;

        if (value > maxValue) {
          maxValue = value;
        }
      }

      result[metric.key] =
        maxValue;
    }

    return result;
  }, [filteredPosts]);

  /*
   * Für das Diagramm werden zwei Werte erzeugt:
   *
   * rawValue:
   * tatsächlicher Wert des Posts
   *
   * scaledValue:
   * relative Balkenbreite von 0 bis 100,
   * bezogen auf das Maximum derselben Kennzahl
   *
   * CTR und Engagement Rate werden in den Daten
   * als Dezimalwerte gespeichert:
   * 0.08 = 8 %
   */
  const chartData = useMemo(() => {
    if (!currentPost) {
      return [];
    }

    return METRICS.map(
      (metric) => {
        const rawValue =
          currentPost[
            metric.key
          ] ?? 0;

        const maxValue =
          metricMaximums[
            metric.key
          ] ?? 0;

        const scaledValue =
          maxValue > 0
            ? (rawValue /
                maxValue) *
              100
            : 0;

        const displayValue =
          metric.isPercentValue
            ? rawValue * 100
            : rawValue;

        const formattedValue =
          metric.isPercentValue
            ? `${displayValue.toLocaleString(
                "de-DE",
                {
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 2,
                }
              )} %`
            : displayValue.toLocaleString(
                "de-DE",
                {
                  maximumFractionDigits: 0,
                }
              );

        return {
          key: metric.key,
          label: metric.label,
          scaledValue,
          formattedValue,
        };
      }
    );
  }, [
    currentPost,
    metricMaximums,
  ]);

  const goPrev = () => {
    setPostIndex((i) =>
      Math.max(
        0,
        i - 1
      )
    );
  };

  const goNext = () => {
    setPostIndex((i) =>
      Math.min(
        sortedPosts.length - 1,
        i + 1
      )
    );
  };

  return (
    <div>
      <div className="filter-button-row">
        {accountOptions.map(
          (account) => (
            <button
              key={account}
              className={`filter-button ${
                selectedAccount ===
                account
                  ? "selected"
                  : ""
              }`}
              onClick={() =>
                setSelectedAccount(
                  account
                )
              }
            >
              {account}
            </button>
          )
        )}
      </div>

      <div
        style={{
          marginTop: 16,
        }}
      >
        {isLoading && (
          <p>
            Lade Daten...
          </p>
        )}

        {error && (
          <p
            style={{
              color: "red",
            }}
          >
            {error}
          </p>
        )}

        {!isLoading &&
          !error &&
          sortedPosts.length ===
            0 && (
            <p>
              Keine Posts für diese
              Auswahl vorhanden.
            </p>
          )}

        {!isLoading &&
          !error &&
          currentPost && (
            <>
              <div
                className="post-performance-nav"
                style={{
                  display: "flex",
                  alignItems:
                    "center",
                  justifyContent:
                    "space-between",
                  marginBottom: 20,
                }}
              >
                <button
                  onClick={goPrev}
                  disabled={
                    postIndex === 0
                  }
                  aria-label="Vorheriger Post"
                >
                  ‹
                </button>

                <div
                  style={{
                    textAlign:
                      "center",
                    flex: 1,
                    padding:
                      "0 12px",
                  }}
                >
                  <div
                    style={{
                      fontSize: 13,
                      color:
                        "#6b7280",
                    }}
                  >
                    Post{" "}
                    {postIndex + 1} /{" "}
                    {
                      sortedPosts.length
                    }{" "}
                    —{" "}
                    {selectedAccount}
                  </div>

                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 14,
                      overflow:
                        "hidden",
                      textOverflow:
                        "ellipsis",
                      whiteSpace:
                        "nowrap",
                    }}
                  >
                    {currentPost.title
                      ? `${
                          currentPost.title.substring(
                            0,
                            40
                          )
                        }${
                          currentPost
                            .title
                            .length >
                          40
                            ? "..."
                            : ""
                        }`
                      : "Ohne Titel"}
                  </div>

                  <div
                    style={{
                      fontSize: 12,
                      color:
                        "#6b7280",
                    }}
                  >
                    {new Date(
                      currentPost.date
                    ).toLocaleDateString(
                      "de-DE"
                    )}
                  </div>
                </div>

                <button
                  onClick={goNext}
                  disabled={
                    postIndex ===
                    sortedPosts.length -
                      1
                  }
                  aria-label="Nächster Post"
                >
                  ›
                </button>
              </div>

              <ResponsiveContainer
                width="100%"
                height={
                  METRICS.length *
                    48 +
                  20
                }
              >
                <BarChart
                  data={chartData}
                  layout="vertical"
                  margin={{
                    top: 8,
                    right: 90,
                    left: 16,
                    bottom: 8,
                  }}
                >
                  {/*
                   * Die X-Achse dient nur noch der internen
                   * Skalierung der Balken von 0 bis 100.
                   * Sie wird nicht angezeigt, da jede Kennzahl
                   * eine eigene Vergleichsbasis besitzt.
                   */}
                  <XAxis
                    type="number"
                    domain={[0, 100]}
                    hide
                  />

                  <YAxis
                    type="category"
                    dataKey="label"
                    width={120}
                    tick={{
                      fontSize: 12,
                    }}
                    axisLine={false}
                    tickLine={false}
                  />

                  <Bar
                    dataKey="scaledValue"
                    barSize={20}
                    fill="#008877"
                    radius={[
                      0,
                      4,
                      4,
                      0,
                    ]}
                  >
                    <LabelList
                      dataKey="formattedValue"
                      position="right"
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </>
          )}
      </div>
    </div>
  );
}